<template>
  <div class="animated fadeIn">
    <VTables
      :title="$t('organizations')"
      :subtitle="$t('all_organizations')"
      :add-text="$t('add_organization')"
      :edit-text="$t('edit_organization')"
      :columns="tableColumns"
      :action-edit="authStore.authenticatedUser?.is_manufacturer || authStore.authenticatedUser?.is_admin"
      :show-add-button="authStore.authenticatedUser?.is_manufacturer || authStore.authenticatedUser?.is_admin"
      :make-form="makeForm"
      :table-options="tableOptions"
      :data="organizationStore.mappedData"
      :add="organizationStore.add"
      :update="organizationStore.update"
      :default-sort="{ column: 'name', ascending: true }"
    >
      <template #reference="{ row }">
        <span v-if="row.reference">{{ row.reference }}</span>
        <div v-else class="small text-body-secondary">{{ $t("no_reference") }}</div>
      </template>
      <template #addresses="{ row }">
        <Popover
          :text="$t('view_addresses')"
        >
          <CButton
            v-if="authStore.authenticatedUser?.is_admin"
            class="btn-space btn-sm"
            color="secondary"
            @click.stop.prevent="showAddressModal(row)"
          >
            <i class="fa fa-map-marker" />
          </CButton>
        </Popover>
      </template>
    </VTables>
    <AddressListModal
      :show="visibleAddressModal"
      :data="selectedOrganization?.addresses"
      :add="
        v => organizationStore.addAndActivateAddress(selectedOrganizationId, v).then(
          () => {
            organizationStore.fetchByIds([selectedOrganizationId])
            addressStore.fetchByOrganizationId(selectedOrganizationId)
          }
        )
      "
      :update="
        v => addressStore.update(v, {
          withoutOptimistic: true,
          onSuccess: () => addressStore.fetchByOrganizationId(selectedOrganizationId)
        })
      "
      @close="visibleAddressModal = false"
      @open="visibleAddressModal = true"
    />
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from "vue-i18n"
import { useMeta } from "vue-meta"
import { onMounted, ref, computed } from "vue-demi"
import type { Organization, VTableOptions } from "@/interfaces"
import {
  organizationStore, addressStore, manufacturerStore,
  pricingStore, authStore
} from "@/store"
import { featureConfigs } from "@/configs"
import VTables from "@/components/VTables.vue"
import AddressListModal from "@/components/modals/address/AddressListModal.vue"
import {
  booleanCustomSorting,
  dateToFilterString,
  generateFilterAlgorithm,
  getListColumns
} from "@/libraries/helpers"
import Popover from "@/components/Popover.vue"

const i18n = useI18n()

useMeta({ title: i18n.t("organizations") })

const visibleAddressModal = ref<boolean>(false)
const selectedOrganizationId = ref<number | null>(null)

const tableColumns = [
  "name",
  "nickname",
  "domain",
  "email",
  "phone",
  "company_number",
  "tax_number",
  "boolean__is_active",
  "boolean__is_verified",
  "boolean__is_company",
  "boolean__is_manufacturer",
  "boolean__is_customer",
  "boolean__is_internal",
  "date__created",
  "date__updated",
  "addresses",
  "actions",
]
const tableOptions: VTableOptions = {
  headings: {
    name: i18n.t("name"),
    date__created: i18n.t("created"),
    date__updated: i18n.t("updated"),
    actions: i18n.t("actions"),
    nickname: i18n.t("nickname"),
    domain: i18n.t("domain"),
    email: i18n.t("email"),
    phone: i18n.t("phone"),
    company_number: i18n.t("company_number"),
    tax_number: i18n.t("tax_number"),
    boolean__is_active: i18n.t("is_active"),
    boolean__is_verified: i18n.t("is_verified"),
    boolean__is_company: i18n.t("is_company"),
    boolean__is_customer: i18n.t("is_customer"),
    boolean__is_internal: i18n.t("is_internal"),
    boolean__is_manufacturer: i18n.t("is_manufacturer"),
    addresses: i18n.t("addresses"),
  },
  sortable: [
    "name",
    "nickname",
    "domain",
    "email",
    "phone",
    "company_number",
    "tax_number",
    "boolean__is_active",
    "boolean__is_verified",
    "boolean__is_company",
    "boolean__is_manufacturer",
    "boolean__is_customer",
    "boolean__is_internal",
    "date__created",
    "date__updated",
  ],
  filterable: tableColumns.filter(column => !["addresses", "actions"].includes(column)),
  get filterAlgorithm() {
    return {
      ...generateFilterAlgorithm(this.filterable, "boolean"),
      ...generateFilterAlgorithm(this.filterable, "date"),
    }
  },
  get listColumns() {
    return getListColumns(this.filterable)
  },
  customSorting: {
    boolean__is_active: (ascending: boolean) => (a: Organization, b: Organization) =>
      booleanCustomSorting(ascending, a, b, "is_active"),
    boolean__is_verified: (ascending: boolean) => (a: Organization, b: Organization) =>
      booleanCustomSorting(ascending, a, b, "is_verified"),
    boolean__is_customer: (ascending: boolean) => (a: Organization, b: Organization) =>
      booleanCustomSorting(ascending, a, b, "is_customer"),
    boolean__is_company: (ascending: boolean) => (a: Organization, b: Organization) =>
      booleanCustomSorting(ascending, a, b, "is_company"),
    boolean__is_internal: (ascending: boolean) => (a: Organization, b: Organization) =>
      booleanCustomSorting(ascending, a, b, "is_internal"),
    boolean__is_manufacturer:
      (ascending: boolean) => (a: Organization, b: Organization) =>
        booleanCustomSorting(ascending, a, b, "is_manufacturer"),
  },
  customFilters: [
    {
      name: "all",
      callback(row: Organization, query: string) {
        return [
          row.name || "",
          row.nickname || "",
          row.domain || "",
          row.email || "",
          row.phone || "",
          row.company_number || "",
          row.tax_number || "",
          row.is_active,
          row.is_verified,
          row.is_company,
          row.is_manufacturer,
          row.is_customer,
          row.is_internal,
          dateToFilterString(row, "created"),
          dateToFilterString(row, "updated"),
          row.addresses || "",
        ]
          .join("###")
          .toLowerCase()
          .includes(query.toLowerCase())
      },
    },
  ],
}

const makeForm = data => {
  const fields: any = {
    id: {
      type: "hidden",
      defaultValue: data?.id,
    },
    name: {
      type: "text",
      label: i18n.t("name"),
      validations: ["required"],
      defaultValue: data?.name,
    },
    nickname: {
      type: "text",
      label: i18n.t("nickname"),
      validations: ["required"],
      defaultValue: data?.nickname,
    },
    domain: {
      type: "text",
      label: i18n.t("domain"),
      validations: ["required"],
      defaultValue: data?.domain,
    },
    email: {
      type: "email",
      label: i18n.t("email"),
      validations: ["required"],
      defaultValue: data?.email,
    },
    phone: {
      type: "text",
      label: i18n.t("phone"),
      validations: ["required"],
      defaultValue: data?.phone,
    },
    company_number: {
      type: "text",
      label: i18n.t("company_number"),
      defaultValue: data?.company_number,
    },
    tax_number: {
      type: "text",
      label: i18n.t("tax_number"),
      defaultValue: data?.tax_number,
    },
    manufacturers: {
      type: "multiselect",
      label: i18n.t("manufacturers"),
      placeholder: i18n.t("select_or_start_typing"),
      options: manufacturerStore.all,
      defaultValue: data?.manufacturers,
      selectOptions: {
        multiple: true,
        trackBy: "id",
        label: "name",
        hideSelected: true,
        searchable: true,
        selectLabel: i18n.t("press_enter_select"),
        selectedLabel: i18n.t("selected"),
        deselectLabel: i18n.t("press_enter_remove"),
        closeOnSelect: false,
      },
    },
  }
  if (featureConfigs.pricings?.enabled) {
    fields.pricings = {
      type: "multiselect",
      label: i18n.t("pricings"),
      placeholder: i18n.t("select_or_start_typing"),
      options: pricingStore.all,
      defaultValue: data?.pricings,
      selectOptions: {
        multiple: true,
        trackBy: "id",
        label: "name",
        hideSelected: true,
        searchable: true,
        selectLabel: i18n.t("press_enter_select"),
        selectedLabel: i18n.t("selected"),
        deselectLabel: i18n.t("press_enter_remove"),
        closeOnSelect: false,
      },
    }
  }
  return {
    id: "admin-organizations-page-form",
    fields
  }
}

const selectedOrganization = computed<Organization>(() =>
  organizationStore.mappedData?.find(o => o.id == selectedOrganizationId.value)
)

function showAddressModal(row: Organization) {
  visibleAddressModal.value = true
  selectedOrganizationId.value = row.id
}

onMounted(() => {
  if ((addressStore.all || []).length === 0) addressStore.fetchAll()
  if ((manufacturerStore.all || []).length === 0) manufacturerStore.fetchAll()
  if (featureConfigs.pricings?.enabled && (pricingStore.all || []).length === 0) pricingStore.fetchAll()
})
</script>
